/**
 * BLANC SPACE CUSTOM: newsletter popup v2 (sections/bs-newsletter-popup.liquid).
 *
 * Shape and rules (2 Nov 2026, from the retired Klaviyo form that converted
 * 12.5% of mobile viewers): first page for every new visitor after a short
 * delay, a pink card over the page, one tap (the opener) before the email, or
 * straight to the email on the 'email' arm, then phone model (only when none
 * is saved), birthday, and a "You're in" screen.
 * A closed popup leaves the "FREE CASE?" bubble and comes back after N days.
 *
 * Openers rotate per browser (data-opener-mode="rotate") and stay fixed for
 * that browser so the PostHog comparison is clean; every event carries
 * bs_nl_opener and bs_nl_version, and the opener is registered as a PostHog
 * super property so later add-to-carts can be split by it. All bs_nl_* events
 * are UNSAMPLED (snippets/posthog.liquid) until the winner is picked.
 *
 * No Klaviyo script is loaded here: submissions go to Klaviyo's client
 * subscriptions API. When klaviyo.js (the onsite tracking embed) is present
 * the subscriber is identified to it so browse/site abandonment can fire.
 */

const STATE_KEY = 'blancspace.newsletter';
const SESSION_KEY = 'bs.nl.session';
const KLAVIYO_REVISION = '2024-10-15';
const VERSION = 'v2';
// 'email' is the no-question arm: the popup opens straight on the email step.
const OPENERS = ['team', 'gift', 'email'];
const DAY_MS = 86400000;
// A superhuman submit is delayed to this floor rather than rejected: browser
// autofill legitimately beats any human threshold.
const MIN_SUBMIT_MS = 1200;
// The phone shortlist on the model step, most add-to-carts first (PostHog
// add_to_cart by phone_model, 60 days to 2 Nov 2026). Only names that exist in
// the menu-built option list are ever shown; refresh this when a new model
// launches. It ranks, it is not a model list: the options come from the menu.
const POPULAR_MODELS = [
  'iPhone 18 Pro Max', 'iPhone 18 Pro', 'iPhone 17', 'iPhone 17 Pro Max', 'iPhone 17 Pro', 'iPhone 16',
  'iPhone 15', 'iPhone 16 Pro', 'iPhone 16 Pro Max', 'iPhone 14',
  'Samsung S26 Ultra', 'Samsung S26', 'Samsung S25 Ultra', 'Samsung S24 Ultra', 'Samsung S25', 'Samsung S24',
];
const HOLD_RETRY_MS = 3000;
const PANEL_EASE = 'cubic-bezier(0.4, 0, 0.22, 1)';
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const readState = () => {
  try {
    return JSON.parse(localStorage.getItem(STATE_KEY) || '{}') || {};
  } catch (error) {
    return {};
  }
};

// `at` is the dismissal clock: a write that is not a new status (hiding the
// teaser, counting) passes touch: false so it cannot extend the wait.
const writeState = (patch, { touch = true } = {}) => {
  try {
    const next = { ...readState(), ...patch };
    if (touch) next.at = Date.now();
    localStorage.setItem(STATE_KEY, JSON.stringify(next));
  } catch (error) {
    // Storage unavailable: the popup simply re-arms next visit.
  }
};

/* Once-per-tab flags (teaser shown, hold reason) so a long browse does not
   repeat the same event on every page. The in-memory set keeps it to once
   per page even where sessionStorage is blocked (the hold loop retries every
   3 s and these events are unsampled). */
const seenThisPage = new Set();
const sessionOnce = (flag) => {
  if (seenThisPage.has(flag)) return false;
  seenThisPage.add(flag);
  try {
    const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}');
    const seen = stored && typeof stored === 'object' ? stored : {};
    if (seen[flag]) return false;
    seen[flag] = 1;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(seen));
  } catch (error) {
    // Storage unavailable: once per page is the best we can do.
  }
  return true;
};

/* Klaviyo already knows this browser: its __kla_id cookie carries an email,
   phone or exchange id once a profile is identified (a click from one of our
   emails sets it too). Malformed cookies are treated as unknown. */
const klaviyoKnowsVisitor = () => {
  try {
    const raw = document.cookie.split('; ').find((part) => part.startsWith('__kla_id='));
    if (!raw) return false;
    const data = JSON.parse(atob(decodeURIComponent(raw.slice('__kla_id='.length))));
    return Boolean(data && (data.$exchange_id || data.$email || data.$phone_number));
  } catch (error) {
    return false;
  }
};

/* Shopify sends the footer's newsletter form back to the page with
   ?customer_posted=true: that visitor has just signed up another way. */
const signedUpInFooter = () => {
  try {
    return new URLSearchParams(location.search).get('customer_posted') === 'true';
  } catch (error) {
    return false;
  }
};

/* ?bs_nl=show opens the popup on any page for checking it, whatever this
   browser's stored state; nothing the check does is saved. */
const qaShow = () => {
  try {
    return new URLSearchParams(location.search).get('bs_nl') === 'show';
  } catch (error) {
    return false;
  }
};

const arrivedFromEmail = () => {
  try {
    const params = new URLSearchParams(location.search);
    const medium = (params.get('utm_medium') || '').toLowerCase();
    const source = (params.get('utm_source') || '').toLowerCase();
    return params.has('_kx') || medium === 'email' || medium === 'sms' || source === 'klaviyo';
  } catch (error) {
    return false;
  }
};

class BsNewsletterPopup extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._teaser = document.querySelector('[data-nl-teaser]');
    this._teaserOpen = this._teaser?.querySelector('[data-nl-teaser-open]') || this._teaser;
    this._qa = qaShow();
    this._model = '';
    this._email = '';
    this._answer = '';
    this._brandHint = '';
    this._step = '';
    this._openedAt = 0;
    this._lastAddAt = 0;
    this._emailFocused = false;
    this._opener = this._resolveOpener();
    this.setAttribute('data-opener', this._opener);

    this.querySelectorAll('[data-nl-close]').forEach((button) => {
      button.addEventListener('click', () => this.hide(button.getAttribute('data-nl-close-method') || 'x'), { signal });
    });
    this.querySelector('[data-nl-overlay]')?.addEventListener('click', () => this.hide('overlay'), { signal });
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || this.hidden) return;
        // An Escape meant for a viewer, the Customily modal or a drawer that
        // opened above the popup must not also close the popup.
        if (document.querySelector('dialog[open]')) return;
        if (!this.contains(event.target) && event.target !== document.body) return;
        this.hide('escape');
      },
      { signal }
    );

    // Keep keyboard focus inside the open dialog (aria-modal promises it): Tab
    // cycles through the visible step's controls and the close button, and
    // focus that lands on the page behind is pulled back to the card. Safari's
    // Tab skips buttons by default, so the focusin guard matters there.
    const focusables = () =>
      Array.from(this.querySelectorAll('button, [href], input, select')).filter(
        (el) => !el.disabled && el.getAttribute('tabindex') !== '-1' && !el.closest('[hidden]') && el.getClientRects().length
      );
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Tab' || this.hidden) return;
        const list = focusables();
        if (!list.length) return;
        const index = list.indexOf(document.activeElement);
        const next = event.shiftKey
          ? list[index <= 0 ? list.length - 1 : index - 1]
          : list[index === -1 || index === list.length - 1 ? 0 : index + 1];
        event.preventDefault();
        next.focus({ preventScroll: true });
      },
      { signal }
    );
    document.addEventListener(
      'focusin',
      (event) => {
        if (this.hidden || this.contains(event.target)) return;
        this.querySelector('.bs-nl__panel')?.focus({ preventScroll: true });
      },
      { signal }
    );

    // iPhone keyboard. Safari scrolls the page to the focused field and the
    // fixed card slides up under the address bar (Scott's phone, 2 Nov 2026);
    // measuring innerHeight against the visual viewport no longer detects the
    // keyboard on current iOS, which shrinks both. So: while a text field in
    // the card has focus on a phone, the card is pinned to the VISUAL viewport
    // (its top and height, written as CSS variables) and drops its pictures.
    // Focus alone is not enough: iPhone's hide-keyboard key leaves the field
    // focused, which left a tall, picture-less card (Scott, 2 Nov 2026). The
    // keyboard is up only while the visible area is clearly shorter than the
    // tallest it has been at this width.
    const phone = window.matchMedia('(max-width: 699px)');
    let viewportFrame = 0;
    let fullHeight = 0;
    let fullWidth = 0;
    const syncKeyboard = () => {
      if (viewportFrame) return;
      viewportFrame = requestAnimationFrame(() => {
        viewportFrame = 0;
        if (this.hidden) return;
        const vv = window.visualViewport;
        const height = vv ? vv.height : window.innerHeight;
        const width = vv ? vv.width : window.innerWidth;
        if (Math.abs(width - fullWidth) > 2) {
          fullWidth = width;
          fullHeight = height;
        }
        fullHeight = Math.max(fullHeight, height);
        const active = document.activeElement;
        const focused = this.contains(active) && active.matches('[data-nl-email], [data-nl-bday-dd], [data-nl-bday-mm]');
        const keyboardUp = fullHeight - height > 150;
        if (vv) {
          this.style.setProperty('--bs-nl-vv-top', `${Math.round(vv.offsetTop)}px`);
          this.style.setProperty('--bs-nl-vv-h', `${Math.round(height)}px`);
        }
        this.toggleAttribute('data-keyboard', phone.matches && focused && keyboardUp);
      });
    };
    this.addEventListener('focusin', syncKeyboard, { signal });
    this.addEventListener('focusout', () => setTimeout(syncKeyboard, 60), { signal });
    window.visualViewport?.addEventListener('resize', syncKeyboard, { signal });
    window.visualViewport?.addEventListener('scroll', syncKeyboard, { signal });

    // iPhone ignores overflow:hidden on the page for touch scrolling, so a
    // drag on the card or the backdrop scrolled the page underneath. Cancel
    // any touchmove inside the popup unless it is scrolling the card's text
    // area, and that only while the area can still move in that direction.
    let touchY = 0;
    this.addEventListener('touchstart', (event) => {
      touchY = event.touches[0]?.clientY || 0;
    }, { signal, passive: true });
    this.addEventListener('touchmove', (event) => {
      if (event.touches.length > 1) return; // pinch zoom stays available
      const body = event.target.closest?.('.bs-nl__body');
      if (body && body.scrollHeight > body.clientHeight + 1) {
        const dy = (event.touches[0]?.clientY || 0) - touchY;
        const atTop = body.scrollTop <= 0;
        const atBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 1;
        if (!(dy > 0 && atTop) && !(dy < 0 && atBottom)) return;
      }
      if (event.cancelable) event.preventDefault();
    }, { signal, passive: false });

    // 1 · Opener answers (buttons carry data-nl-answer)
    this.querySelector('[data-nl-step="opener"]')?.addEventListener(
      'click',
      (event) => {
        const answer = event.target.closest('[data-nl-answer]');
        if (answer) this._answerOpener(answer);
      },
      { signal }
    );

    // 2 · Email
    const emailInput = this.querySelector('[data-nl-email]');
    emailInput?.addEventListener(
      'focus',
      () => {
        if (this._emailFocused) return;
        this._emailFocused = true;
        this._track('bs_nl_email_focused', { bs_nl_ms_since_open: Date.now() - this._openedAt });
      },
      { signal }
    );
    this.querySelector('[data-nl-step="email"]')?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this._submitEmail();
      },
      { signal }
    );

    // 3 · Phone model: bubbles, full list, skip
    this.querySelector('[data-nl-bubbles]')?.addEventListener(
      'click',
      (event) => {
        const bubble = event.target.closest('[data-nl-model]');
        if (bubble) this._chooseModel(bubble.getAttribute('data-nl-model'), 'bubble');
      },
      { signal }
    );
    this._buildBubbles();
    this.querySelector('[data-nl-model-select]')?.addEventListener(
      'change',
      (event) => {
        if (event.target.value) this._chooseModel(event.target.value, 'list');
      },
      { signal }
    );
    this.querySelector('[data-nl-skip-model]')?.addEventListener(
      'click',
      () => {
        this._track('bs_nl_model_skipped');
        this._showStep('birthday');
      },
      { signal }
    );

    // 4 · Birthday
    this.querySelector('[data-nl-step="birthday"]')?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this._submitBirthday();
      },
      { signal }
    );
    this.querySelector('[data-nl-skip-birthday]')?.addEventListener(
      'click',
      () => {
        this._track('bs_nl_birthday_skipped');
        this._finish();
      },
      { signal }
    );
    // Day and month boxes with a fixed slash between them (the iPhone number
    // pad has no "/"). Digits only; two day digits, or a first digit the day
    // cannot start with (4-9), move on to the month; backspace in an empty
    // month goes back to the day. A pasted "24/06" or "2406" fills both.
    const dayInput = this.querySelector('[data-nl-bday-dd]');
    const monthInput = this.querySelector('[data-nl-bday-mm]');
    const focusEnd = (input) => {
      input.focus({ preventScroll: true });
      const end = input.value.length;
      try {
        input.setSelectionRange(end, end);
      } catch (error) {
        // Some input types refuse a selection; the focus is what matters.
      }
    };
    dayInput?.addEventListener(
      'input',
      () => {
        const digits = dayInput.value.replace(/\D/g, '');
        if (digits.length > 2) {
          // Pasted or autofilled a whole date.
          dayInput.value = digits.slice(0, 2);
          if (monthInput) monthInput.value = digits.slice(2, 4);
          if (monthInput) focusEnd(monthInput);
          return;
        }
        let day = digits.slice(0, 2);
        if (day.length === 1 && Number(day) > 3) day = `0${day}`;
        dayInput.value = day;
        if (day.length === 2 && monthInput) focusEnd(monthInput);
      },
      { signal }
    );
    monthInput?.addEventListener(
      'input',
      () => {
        let month = monthInput.value.replace(/\D/g, '').slice(0, 2);
        if (month.length === 1 && Number(month) > 1) month = `0${month}`;
        monthInput.value = month;
      },
      { signal }
    );
    monthInput?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Backspace' && monthInput.value === '' && dayInput) {
          event.preventDefault();
          dayInput.value = dayInput.value.slice(0, -1);
          focusEnd(dayInput);
        }
      },
      { signal }
    );
    // Enter (the keypad's "next") in the day box moves on rather than submitting.
    dayInput?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' && monthInput) {
          event.preventDefault();
          focusEnd(monthInput);
        }
      },
      { signal }
    );
    // Tapping anywhere on the pill goes to the box that still needs digits.
    this.querySelector('.bs-nl__date')?.addEventListener(
      'click',
      (event) => {
        if (event.target.matches('input')) return;
        if (dayInput && dayInput.value.length < 2) focusEnd(dayInput);
        else if (monthInput) focusEnd(monthInput);
      },
      { signal }
    );

    // 5 · You're in
    this.querySelector('[data-nl-success-cta]')?.addEventListener(
      'click',
      (event) => {
        const href = event.currentTarget.getAttribute('href') || '';
        this._track('bs_nl_success_cta', { bs_nl_cta_target: href });
      },
      { signal }
    );

    // A dismissed visitor's pages never warm the images (nothing to show
    // them); the finger landing on the teaser is the cue, ~100 ms before the
    // click, so the cases are usually decoded by the time the card appears.
    this._teaserOpen?.addEventListener('pointerdown', () => this._warmImages(), { signal, passive: true });
    this._teaserOpen?.addEventListener('touchstart', () => this._warmImages(), { signal, passive: true });
    this._teaserOpen?.addEventListener(
      'click',
      () => {
        this._warmImages();
        this._track('bs_nl_teaser_clicked');
        this._teaser.hidden = true;
        this.show('teaser');
      },
      { signal }
    );

    // Add to cart just happened: give the added-items sheet room.
    document.addEventListener('variant:add', () => (this._lastAddAt = Date.now()), { signal });

    // Back-forward cache: a page restored with its 5 s timer still pending
    // would open over a shopper who dismissed or subscribed on the next page.
    // Re-arm from the stored state instead (the timer is cleared in _arm).
    window.addEventListener(
      'pageshow',
      (event) => {
        if (!event.persisted || this.hidden === false) return;
        this._arm();
      },
      { signal }
    );

    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener(
        'shopify:section:select',
        (event) => {
          if (event.target.contains(this)) this.show('editor');
        },
        { signal }
      );
      return;
    }

    this._arm();
  }

  disconnectedCallback() {
    clearTimeout(this._timer);
    this._abortController.abort();
  }

  /* --- Analytics -------------------------------------------------------------- */

  _track(event, detail = {}) {
    const payload = {
      bs_nl_version: VERSION,
      bs_nl_opener: this._opener,
      ...(this._answer ? { bs_nl_answer: this._answer } : {}),
      ...detail,
    };
    try {
      (window.dataLayer = window.dataLayer || []).push({ event, ...payload });
    } catch (error) {
      // Analytics must never break signup.
    }
    try {
      window.posthog?.capture?.(event, payload);
    } catch (error) {
      // Same rule.
    }
  }

  /* --- Opener rotation --------------------------------------------------------- */

  _resolveOpener() {
    const mode = this.getAttribute('data-opener-mode') || 'rotate';
    if (OPENERS.includes(mode)) return mode;
    const stored = readState().opener;
    if (OPENERS.includes(stored)) return stored;
    const picked = OPENERS[Math.floor(Math.random() * OPENERS.length)];
    writeState({ opener: picked });
    return picked;
  }

  /* --- Triggering ---------------------------------------------------------- */

  /* Dismissed less than reshow_days ago: the teaser, not the popup. */
  _dismissedRecently(state) {
    if (state.status !== 'dismissed') return false;
    // First close: reshow_days (5). Closed again after it came back: the
    // longer reshow_days_repeat (30), so a clear "no" is not asked weekly.
    const first = Number(this.getAttribute('data-reshow-days')) || 5;
    const repeat = Number(this.getAttribute('data-reshow-days-repeat')) || 30;
    const days = (Number(state.dismissals) || 1) >= 2 ? repeat : first;
    return Date.now() - (Number(state.at) || 0) < days * DAY_MS;
  }

  _arm() {
    clearTimeout(this._timer);
    // No Klaviyo key or list (cleared in the editor) means every submit would
    // fail: better no popup at all than one that cannot sign anyone up.
    if (!this._klaviyoKey || !this.getAttribute('data-klaviyo-list')) return;

    if (this._qa) {
      this._warmImages();
      this._timer = setTimeout(() => this.show('qa'), 1000);
      return;
    }

    if (signedUpInFooter()) {
      writeState({ status: 'subscribed' });
      return;
    }

    const state = readState();
    if (state.status === 'subscribed' || state.status === 'known') return;

    // People we already have: a click from one of our emails, or a browser
    // Klaviyo has already identified. Remembered so later pages skip quietly.
    if (arrivedFromEmail() || klaviyoKnowsVisitor()) {
      writeState({ status: 'known' });
      return;
    }

    // Never on the basket page; checkout is not theme-rendered.
    if (this.getAttribute('data-page-type') === 'cart') return;

    if (this._dismissedRecently(state)) {
      this._showTeaser();
      return;
    }

    // The card's case images are lazy in the hidden popup and would only start
    // downloading as it opens. Fetch them once the page has loaded, so they are
    // ready in time without competing with the page's own first images.
    const warm = () => this._warmImages();
    if (document.readyState === 'complete') warm();
    else window.addEventListener('load', warm, { once: true });

    const fire = () => {
      clearTimeout(this._timer);
      // The state can have changed while the timer waited (another tab, or
      // a page restored from the back-forward cache): read it again.
      const now = readState();
      if (now.status === 'subscribed' || now.status === 'known') return;
      if (this._dismissedRecently(now)) {
        this._showTeaser();
        return;
      }
      const hold = this._holdReason();
      if (hold) {
        if (sessionOnce(`held:${hold}`)) this._track('bs_nl_held', { bs_nl_hold_reason: hold });
        this._timer = setTimeout(fire, HOLD_RETRY_MS);
        return;
      }
      this.show('auto');
    };
    this._timer = setTimeout(fire, (Number(this.getAttribute('data-delay')) || 5) * 1000);
  }

  _warmImages() {
    this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  /* Never stack two prompts or interrupt a purchase step. */
  _holdReason() {
    // Pandectes draws its banner inside <pandectes-cmp>'s shadow root (the old
    // #pandectes-banner id is gone, so v1's check never fired); the banner's
    // buttons have height only while it is waiting for an answer.
    const consentBanner = document.getElementById('pandectes-banner');
    if (consentBanner && consentBanner.getBoundingClientRect().height > 0) return 'consent_banner';
    const cmpRoot = document.querySelector('pandectes-cmp')?.shadowRoot;
    if (cmpRoot && [...cmpRoot.querySelectorAll('button')].some((b) => b.getBoundingClientRect().height > 0)) return 'consent_banner';
    if (document.documentElement.classList.contains('bsp-lock')) return 'personaliser';
    // Surfaces that paint ABOVE the popup's z-index: the Customily modal, the
    // top-layer video / review viewers, an open chat widget.
    const customily = document.querySelector('.customily-modal-container');
    if (customily && customily.getBoundingClientRect().height > 0) return 'customily';
    if (document.querySelector('dialog[open]')) return 'viewer';
    if (document.documentElement.getAttribute('data-bs-chat-state') === 'open') return 'chat';
    if (document.querySelector('.drawer[open], .popover[open], cart-notification-drawer[open]')) return 'drawer';
    if (document.querySelector('store-header[expanded]')) return 'menu';
    if (Date.now() - this._lastAddAt < 20000) return 'just_added';
    if (document.visibilityState === 'hidden') return 'tab_hidden';
    return '';
  }

  _showTeaser() {
    // No x on the teaser (Scott, 2 Nov 2026): only signing up removes it.
    if (!this._teaser) return;
    this._teaser.hidden = false;
    if (sessionOnce('teaser_shown')) this._track('bs_nl_teaser_shown');
  }

  show(reason) {
    if (!this.hidden) return;
    // A saved model (menu pick, PDP or search inference, shop-by-phone visit)
    // is never asked again; visual-only brand guesses still get the question.
    const knownModel = window.BlancSpaceDevicePreference?.get?.() || '';
    if (knownModel && !this._model) {
      this._model = knownModel;
      this._track('bs_nl_model_prefilled', { bs_nl_model: knownModel });
    }

    this.querySelectorAll('[data-nl-opener]').forEach((block) => {
      block.hidden = block.getAttribute('data-nl-opener') !== this._opener;
    });

    // The bubble is a deliberate tap: straight to the email. Otherwise the
    // opener first, unless it was already answered earlier in this page.
    const first =
      this._email ? 'success' : reason === 'teaser' || this._answer || this._opener === 'email' ? 'email' : 'opener';
    this._showStep(first);
    this.hidden = false;
    this._openedAt = Date.now();
    document.documentElement.style.overflow = 'hidden';
    try {
      window.posthog?.register?.({ bs_nl_opener: this._opener, bs_nl_version: VERSION });
    } catch (error) {
      // Analytics only.
    }
    this._track('bs_nl_view', { bs_nl_reason: reason, bs_nl_first_step: first, bs_nl_model_known: !!this._model });
    this._trackStep(first);
    // Focus the card itself, not its first button: a focused button shows a
    // ring on iPhone before anyone has touched it. Tab still starts inside.
    this.querySelector('.bs-nl__panel')?.focus({ preventScroll: true });
  }

  hide(method = 'x') {
    if (this.hidden) return;
    const step = this._currentStep();
    this.hidden = true;
    this.removeAttribute('data-keyboard');
    document.documentElement.style.overflow = '';
    this.style.removeProperty('--bs-nl-vv-top');
    this.style.removeProperty('--bs-nl-vv-h');
    const state = readState();
    const dismissals = (Number(state.dismissals) || 0) + 1;
    if (state.status !== 'subscribed' && !this._qa) {
      writeState({ status: 'dismissed', dismissals });
      this._showTeaser();
    }
    this._track('bs_nl_dismissed', {
      bs_nl_step: step,
      bs_nl_close_method: method,
      bs_nl_open_ms: Date.now() - this._openedAt,
      bs_nl_subscribed: state.status === 'subscribed',
      bs_nl_dismiss_count: dismissals,
    });
  }

  /* --- Steps ---------------------------------------------------------------- */

  /* The step the shopper is on; the DOM shows two steps for 200 ms mid-slide. */
  _currentStep() {
    return this._step || '';
  }

  _trackStep(name) {
    const order = ['opener', 'email', 'model', 'birthday', 'success'];
    this._track('bs_nl_step_viewed', { bs_nl_step: name, bs_nl_step_index: order.indexOf(name) + 1 });
  }

  /**
   * The top band stays put while the step content slides out left and the
   * next slides in from the right. Steps share one grid cell so both can
   * overlap mid-flight without the panel height jumping.
   */
  _showStep(name) {
    const steps = Array.from(this.querySelectorAll('[data-nl-step]'));
    const incoming = steps.find((step) => step.getAttribute('data-nl-step') === name);
    const outgoing = steps.find((step) => !step.hidden);
    if (!incoming || incoming === outgoing) return;
    const wasOpen = !this.hidden;

    steps.forEach((step) => {
      if (step !== incoming && step !== outgoing) step.hidden = true;
    });
    incoming.hidden = false;
    this._step = name;
    this.setAttribute('data-step', name);
    // The dialog's accessible name follows the step's own heading.
    const heading = incoming.querySelector('h2');
    if (heading?.id) this.querySelector('.bs-nl__panel')?.setAttribute('aria-labelledby', heading.id);

    if (outgoing) {
      // No animation while the popup is still hidden (initial step choice):
      // it would size the panel by two steps for 200ms (2026-09-07 CLS fix).
      if (reducedMotion() || !outgoing.animate || this.hidden) {
        outgoing.hidden = true;
      } else {
        const exit = outgoing.animate(
          [
            { transform: 'none', opacity: 1 },
            { transform: 'translateX(-36px)', opacity: 0 },
          ],
          { duration: 200, easing: PANEL_EASE, fill: 'forwards' }
        );
        exit.finished
          .catch(() => {})
          .finally(() => {
            outgoing.hidden = true;
            exit.cancel();
          });
        incoming.animate(
          [
            { transform: 'translateX(36px)', opacity: 0 },
            { transform: 'none', opacity: 1 },
          ],
          { duration: 300, easing: 'cubic-bezier(0.2, 0.75, 0.25, 1)' }
        );
      }
    }

    if (wasOpen) this._trackStep(name);
    if (name === 'email' && wasOpen) this.querySelector('[data-nl-email]')?.focus({ preventScroll: true });
    if (name === 'birthday' && wasOpen) this.querySelector('[data-nl-bday-dd]')?.focus({ preventScroll: true });
    if (name === 'success') this._setSuccessLink();
  }

  _answerOpener(button) {
    if (this._step !== 'opener') return; // a second tap while the step slides out
    this._answer = String(button.getAttribute('data-nl-answer') || '').trim();
    if (this._opener === 'team') {
      this._brandHint = this._answer === 'Android' ? 'samsung' : 'apple';
      this._buildBubbles();
    }
    this._track('bs_nl_opener_answered', { bs_nl_ms_since_open: Date.now() - this._openedAt });
    this._showStep('email');
  }

  /** The Klaviyo profile property each opener writes. */
  _answerProperties() {
    if (!this._answer) return {};
    const key = { team: 'team', gift: 'shopping_for' }[this._opener];
    return key ? { [key]: this._answer } : {};
  }

  /**
   * Newest models for the brand: the opener's iPhone/Android answer first,
   * then the detected device brand. Taken from the same canonical menu the
   * header device selector uses (the <select> in this section).
   */
  async _buildBubbles() {
    const container = this.querySelector('[data-nl-bubbles]');
    const select = this.querySelector('[data-nl-model-select]');
    if (!container || !select) return;

    let brand = this._brandHint;
    if (!brand) {
      try {
        await window.BlancSpaceDevicePreference?.visualReady;
      } catch (error) {
        // Detection failing just means the Apple-first fallback.
      }
      brand = window.BlancSpaceDevicePreference?.getDetectedBrand?.() || 'apple';
    }

    const options = Array.from(select.options).filter((option) => option.value);
    let matches = options.filter((option) => option.dataset.phoneBrand === brand);
    if (matches.length === 0) matches = options;
    // Most-bought first (the menu order is newest first, which put the rarely
    // bought iPhone Air on the shortlist); anything unranked keeps menu order.
    const rank = (option) => {
      const index = POPULAR_MODELS.indexOf(option.value);
      return index === -1 ? POPULAR_MODELS.length : index;
    };
    matches = matches
      .map((option, index) => ({ option, index }))
      .sort((a, b) => rank(a.option) - rank(b.option) || a.index - b.index)
      .map((entry) => entry.option);

    container.replaceChildren(
      ...matches.slice(0, 6).map((option) => {
        const bubble = document.createElement('button');
        bubble.type = 'button';
        bubble.className = 'bs-nl__bubble';
        bubble.setAttribute('data-nl-model', option.value);
        bubble.textContent = option.textContent.trim();
        return bubble;
      })
    );
  }

  _chooseModel(model, via) {
    this._model = String(model || '').trim();
    this._track('bs_nl_model_selected', { bs_nl_model: this._model, bs_nl_model_via: via });
    // Explicit pick, same weight as the header menu selector. origin 'popup'
    // makes collection grids and the PDP picker re-render in place behind the
    // popup; nothing reloads.
    window.BlancSpaceDevicePreference?.set(this._model, { source: 'menu', origin: 'popup' });
    if (this._email) this._klaviyoProfilePatch({ phone_model: this._model });
    this._showStep('birthday');
  }

  async _submitEmail() {
    const input = this.querySelector('[data-nl-email]');
    const error = this.querySelector('[data-nl-error]');
    const submit = this.querySelector('[data-nl-email-submit]');
    const email = String(input?.value || '').trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (error) {
        error.textContent = 'Please enter a valid email address.';
        error.hidden = false;
      }
      this._track('bs_nl_email_error', { bs_nl_error: email ? 'invalid' : 'empty' });
      return;
    }
    if (error) error.hidden = true;

    // Honeypot only: a real shopper never sees that field.
    if (this.querySelector('[data-nl-hp]')?.value) {
      this._email = email;
      this._finish();
      return;
    }

    if (submit) submit.disabled = true;
    const elapsed = Date.now() - this._openedAt;
    if (elapsed < MIN_SUBMIT_MS) {
      await new Promise((resolve) => setTimeout(resolve, MIN_SUBMIT_MS - elapsed));
    }
    const result = await this._klaviyoSubscribe(email);
    if (submit) submit.disabled = false;

    if (!result.ok) {
      if (error) {
        error.textContent = result.emailRejected
          ? 'That email address doesn’t look right. Please check it.'
          : 'Something went wrong. Please try again.';
        error.hidden = false;
      }
      // http_4xx/5xx tells a dead integration (key, list, API revision) apart
      // from a flaky connection in PostHog.
      this._track('bs_nl_email_error', { bs_nl_error: result.status ? `http_${result.status}` : 'network' });
      return;
    }

    this._email = email;
    this._klaviyoIdentify(email);
    if (!this._qa) writeState({ status: 'subscribed' });
    if (this._teaser) this._teaser.hidden = true;
    this._track('bs_nl_email_submitted', {
      bs_nl_model_known: !!this._model,
      bs_nl_ms_since_open: Date.now() - this._openedAt,
    });
    try {
      window.posthog?.register?.({ bs_nl_subscribed: true });
    } catch (error) {
      // Analytics only.
    }
    this._showStep(this._model ? 'birthday' : 'model');
  }

  _submitBirthday() {
    const error = this.querySelector('[data-nl-birthday-error]');
    const dayText = String(this.querySelector('[data-nl-bday-dd]')?.value || '').trim();
    const monthText = String(this.querySelector('[data-nl-bday-mm]')?.value || '').trim();
    const match = /^\d{1,2}$/.test(dayText) && /^\d{1,2}$/.test(monthText);
    const day = match ? Number(dayText) : 0;
    const month = match ? Number(monthText) : 0;
    // 29 Feb stays valid: birth years vary.
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (!match || month < 1 || month > 12 || day < 1 || day > daysInMonth[month - 1]) {
      if (error) {
        error.textContent = 'Add your day and month, e.g. 24 / 06.';
        error.hidden = false;
      }
      this._track('bs_nl_birthday_error');
      return;
    }
    if (error) error.hidden = true;

    const dd = String(day).padStart(2, '0');
    const mm = String(month).padStart(2, '0');
    // `Birthday` (a full date) is what Klaviyo's Birthday flow and the
    // "VL | Birthday Club" segment read; Klaviyo's own DD/MM fields store the
    // current year, so this matches them. v1 wrote only birthday_ddmm, which
    // no flow reads (found 2 Nov 2026).
    // 29 February needs a leap year to be a real date; the most recent one
    // keeps it as close to "this year" as the other birthdays.
    const year = mm === '02' && dd === '29' ? 2024 : new Date().getFullYear();
    this._klaviyoProfilePatch({
      Birthday: `${year}-${mm}-${dd}`,
      birthday_ddmm: `${dd}/${mm}`,
      birthday_day: day,
      birthday_month: month,
    });
    this._track('bs_nl_birthday_submitted');
    this._finish();
  }

  _finish() {
    if (!this._qa) writeState({ status: 'subscribed' });
    if (this._teaser) this._teaser.hidden = true;
    this._showStep('success');
  }

  /* Their phone's collection, else the default. */
  _setSuccessLink() {
    const cta = this.querySelector('[data-nl-success-cta]');
    if (!cta || !this._model) return;
    const option = Array.from(this.querySelectorAll('[data-nl-model-select] option')).find((o) => o.value === this._model);
    const href = option?.dataset.collectionUrl || '';
    if (href) cta.setAttribute('href', href);
  }

  /* --- Klaviyo client API ---------------------------------------------------- */

  get _klaviyoKey() {
    return this.getAttribute('data-klaviyo-key') || '';
  }

  /** Resolves to { ok, status, emailRejected }; never throws. */
  async _klaviyoSubscribe(email) {
    if (!this._klaviyoKey) return { ok: false, status: 0 };
    const properties = {
      signup_source: 'Website popup',
      popup_opener: this._opener,
      ...this._answerProperties(),
    };
    if (this._model) properties.phone_model = this._model;

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this._klaviyoKey)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Website popup',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email, properties },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: this.getAttribute('data-klaviyo-list') || '' } },
              },
            },
          }),
        }
      );
      if (response.status === 202 || response.ok) return { ok: true, status: response.status };
      let emailRejected = false;
      if (response.status === 400) {
        try {
          const detail = JSON.stringify((await response.json())?.errors?.[0] || {});
          emailRejected = /email/i.test(detail);
        } catch (parseError) {
          // A 400 with no readable body is reported as a plain failure.
        }
      }
      return { ok: false, status: response.status, emailRejected };
    } catch (error) {
      return { ok: false, status: 0 };
    }
  }

  _klaviyoIdentify(email) {
    // Cookies the visitor for Klaviyo onsite tracking so Active on Site and
    // Viewed Product attach to the new profile. A no-op without the embed.
    try {
      window.klaviyo?.identify?.({ $email: email });
    } catch (error) {
      // Best-effort only.
    }
  }

  _klaviyoProfilePatch(properties) {
    if (!this._klaviyoKey || !this._email) return;
    fetch(`https://a.klaviyo.com/client/profiles?company_id=${encodeURIComponent(this._klaviyoKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: { email: this._email, properties },
        },
      }),
      keepalive: true,
    }).catch(() => {});
  }
}

if (!window.customElements.get('bs-newsletter-popup')) {
  window.customElements.define('bs-newsletter-popup', BsNewsletterPopup);
}
